import React, { ComponentPropsWithoutRef, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

export interface ITab {
  label: string;
  anchorLink: string;
  content: ReactNode;
  isShowLabel?: boolean;
  isDisabled?: boolean;
}

interface IProps extends ComponentPropsWithoutRef<'div'> {
  tabs: ITab[];
}

export const Tabs: React.FC<IProps> = ({ tabs, ...props }) => {
  const { hash } = useLocation();
  const { t } = useTranslation();

  const handleGenAnchorLink = useCallback((link: string) => `#${link.toLowerCase()}`, []);

  const activeTab: ITab = useMemo(() => tabs.find((tabItem) => handleGenAnchorLink(tabItem.anchorLink) === hash) ?? tabs[0], [hash, tabs]);

  return tabs.length ? (
    <>
      <div {...props} className='text-center text-hl-black-70 border-b border-hl-beige'>
        <ul className='flex overflow-x-auto -mb-px no-scrollbar'>
          {tabs.map((tabItem) => (
            <li
              key={tabItem.anchorLink}
              className={['me-2 shrink-0 text-base', tabItem.isDisabled ? 'text-gray-400 rounded-t-lg cursor-not-allowed' : ''].join(' ')}
            >
              <a
                href={handleGenAnchorLink(tabItem.anchorLink)}
                className={[
                  'inline-block border-b-2 rounded-t-lg p-2 md:p-4',
                  activeTab.anchorLink === tabItem.anchorLink
                    ? 'border-hl-primary text-hl-primary font-semibold'
                    : 'border-transparent hover:text-hl-primary/60 hover:border-hl-primary/60'
                ].join(' ')}
              >
                {t(tabItem.label)}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className='w-full px-4 py-6'>
        {activeTab.isShowLabel ?? true ? <h2 className=' text-center mb-4 font-bold text-3xl lg:text-5xl'>{t(activeTab.label)}</h2> : <></>}
        {activeTab.content}
      </div>
    </>
  ) : (
    <></>
  );
};
